import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  MenuItem,
  Box,
  Typography,
  Slide,
  Autocomplete,
  TextField,
  InputBase,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import { useThemeContext } from "../../../Context/ThemeContext";
import { DARKCOLORS, LIGHTCOLORS } from "../../../Utils/constants";
import SuccessNotification from "../../../Components/Common/SuccessNotification";
import { StyledTextField } from "../../../Components/Common/StyledTextField";
import { addUser, updateUser } from "../../../Services/authService";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import LoadingAnimation from "../../../Components/Common/LoadingAnimation";
import countryCodes from "../../../Components/Layouts/full_country_calling_codes.json";
import "./Users.css";

// Slide Transition Function
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="left" ref={ref} {...props} timeout={800} />;
});

function UsersModal({ open, onClose, selectedUser, fetchUsers }) {
  const { t } = useTranslation();
  const { mode } = useThemeContext();

  const btnColor =
    mode === "dark" ? DARKCOLORS.Primary500 : LIGHTCOLORS.Primary500;
  const modalBackgrounColor =
    mode === "dark" ? DARKCOLORS.BackgroundCard : LIGHTCOLORS.BackgroundCard;
  const inputBoxColor =
    mode === "dark" ? DARKCOLORS.GreyScale10 : LIGHTCOLORS.GreyScale10;
  const borderColor2 =
    mode === "dark" ? DARKCOLORS.GreyScale30 : LIGHTCOLORS.GreyScale30;
  const imgBoxColor =
    mode === "dark" ? DARKCOLORS.Secondary700 : LIGHTCOLORS.Secondary700;

  const [formData, setFormData] = useState({
    // id: 0,
    login: "",
    firstName: "",
    lastName: "",
    email: "",
    address: "",
    mobile: "",
    authorities: [],
  });

  const [errors, setErrors] = useState({});
  const [generalError, setGeneralError] = useState("");
  const [loading, setLoading] = useState(false);
  const [successNotification, setSuccessNotification] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null); // File object for the image
  const [countryCode, setCountryCode] = useState({
    name: "Germany",
    dial_code: "+49",
  });
  const [mobileNumber, setMobileNumber] = useState("");

  const handleClose = () => {
    setErrors({});
    setGeneralError("");
    onClose();
  };

  useEffect(() => {
    setGeneralError("");
    setErrors({});
    setImagePreview(null);
    setImageFile(null);
    if (selectedUser) {
      setFormData(selectedUser);

      if (selectedUser.profilePictureUrl) {
        setImagePreview(selectedUser.profilePictureUrl);
      } else {
        setImagePreview(null);
      }
      const fullMobile = selectedUser.mobile || "";
      const matched = countryCodes.find((c) =>
        fullMobile.startsWith(c.dial_code),
      );
      if (matched) {
        setCountryCode(matched);
        setMobileNumber(fullMobile.replace(matched.dial_code, "").trim());
      } else {
        setMobileNumber(fullMobile);
      }
    } else {
      setCountryCode({ name: "Germany", dial_code: "+49" });
      setMobileNumber("");
      setFormData({
        // id: 0,
        login: "",
        firstName: "",
        lastName: "",
        email: "",
        address: "",
        mobile: "",
        authorities: [],
      });
    }
    // "open" dep: re-initialize on every reopen so data typed in a cancelled
    // add/edit doesn't survive close (selectedUser alone may not change).
  }, [selectedUser, open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Special handling for authorities field
    if (name === "authorities") {
      const selectedRoles = typeof value === "string" ? [value] : value; // If it's a string, convert it to an array
      setFormData({
        ...formData,
        [name]: selectedRoles,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.login) newErrors.login = t("users.usernameRequired");
    if (!formData.firstName) newErrors.firstName = t("users.firstNameRequired");
    if (!formData.lastName) newErrors.lastName = t("users.lastNameRequired");
    if (!mobileNumber)
      newErrors.mobile = t("membershipCards.Mobilenumberrequired");
    else if (!/^\d{5,15}$/.test(mobileNumber))
      newErrors.mobile = t("membershipCards.phoneNumberMustBe5to15");
    if (!formData.email) {
      newErrors.email = t("users.emailRequired");
    } else if (!/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/.test(formData.email)) {
      newErrors.email = t("users.invalidEmailFormat");
    }
    if (!formData.address) newErrors.address = t("users.addressRequired");
    if (!formData.authorities || formData.authorities.length === 0)
      newErrors.authorities = t("users.roleRequired");

    setErrors(newErrors);
    setGeneralError(
      Object.keys(newErrors)?.length > 0
        ? newErrors.mobile || t("membershipCards.Pleasefill")
        : "",
    );
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    try {
      setLoading(true);
      formData.mobile = `${countryCode.dial_code} ${mobileNumber}`.trim();

      // Clone the form data object
      const dataToSend = { ...formData };

      // Remove unwanted fields for the update operation
      if (formData.id) {
        delete dataToSend.profilePictureUrl;
        delete dataToSend.activated;
        delete dataToSend.langKey;
        delete dataToSend.createdBy;
        delete dataToSend.createdDate;
        delete dataToSend.lastModifiedBy;
        delete dataToSend.lastModifiedDate;
      }

      // Create FormData object
      const formDataToSend = new FormData();
      formDataToSend.append("userJson", JSON.stringify(dataToSend));
      if (imageFile) formDataToSend.append("file", imageFile);

      if (formData.id) {
        await updateUser(formDataToSend);
      } else {
        await addUser(formDataToSend);
      }

      // Reset the form
      setFormData({
        // id: 0,
        login: "",
        firstName: "",
        lastName: "",
        email: "",
        address: "",
        mobile: "",
        authorities: [],
      });
      setCountryCode({ name: "Germany", dial_code: "+49" });
      setMobileNumber("");

      setSuccessNotification(true); // Show success notification
      onClose(); // Close the dialog
      fetchUsers(); // Refresh the table data
    } catch (error) {
      // setGeneralError(error?.response?.data?.detail);
      const apiMessage = error?.response?.data?.message;
      if (apiMessage === "error.emailexists") {
        setGeneralError(t("users.emailAlreadyExists"));
      } else if (apiMessage === "error.phoneNumberExist") {
        setGeneralError(t("users.phoneNumberAlreadyExists"));
      } else if (apiMessage === "error.userexists") {
        setGeneralError(t("users.usernameAlreadyExists"));
      } else {
        setGeneralError(t("users.errorMessage"));
      }
      console.error(t("users.errorCreatingUser"), error);
    } finally {
      setLoading(false);
    }
  };

  // Handle Image Selection
  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const validImageTypes = [
        "image/jpeg",
        "image/png",
        "image/jpg",
        "image/gif",
      ];
      if (!validImageTypes.includes(file.type)) {
        setErrors({ ...errors, image: t("inventory.validImageFile") });
        return;
      }

      // Generate image preview
      const reader = new FileReader();
      reader.onload = () => {
        setImagePreview(reader.result); // Set the image preview
        setErrors({ ...errors, image: "" }); // Clear image errors
      };
      reader.readAsDataURL(file);
      setImageFile(file);
    }
  };

  return (
    <>
      {loading && <LoadingAnimation />}
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        TransitionComponent={Transition}
        PaperProps={{
          sx: {
            position: "absolute",
            right: 10,
            margin: 0,
            height: "auto", // Full height
            borderRadius: 3,
            background: modalBackgrounColor,
            width: "97%",
            "@media (max-width: 427px)": {
              right: 5,
            },
          },
        }}
      >
        {/* Title with Close Icon */}
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: {
              xs: 2, // base padding
              sm: 3, // small screens
              md: "20px 24px ", // medium screens
            },
          }}
        >
          <span className="franchise-heading-text">
            {selectedUser ? t("users.editUser") : t("users.newUser")}
          </span>
          <IconButton onClick={handleClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            overflowY: "auto",
            paddingTop: 2,
            p: {
              xs: 2, // base padding
              sm: 3, // small screens
              md: "20px 24px ", // medium screens
            },
          }}
        >
          <Box
            component="form"
            noValidate
            autoComplete="off"
            sx={{ display: "flex", flexDirection: "column", gap: 3 }}
          >
            {/* Image Upload
            <Box
              sx={{
                border: `1px dashed ${imgBoxColor}`,
                borderRadius: "8px",
                textAlign: "center",
                cursor: "pointer",
                position: "relative",
                width: "100%",
                height: "200px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
              }}
              onClick={() => document.getElementById("upload-image").click()}
            >
              <input
                type="file"
                id="upload-image"
                style={{ display: "none" }}
                accept="image/*"
                onChange={handleImageUpload}
              />
              {imagePreview ? (
                <img
                  src={imagePreview}
                  alt="Preview"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                    borderRadius: "8px",
                  }}
                />
              ) : (
                <div>
                  <AddPhotoAlternateIcon
                    sx={{
                      fontSize: "8rem",
                      color: "#C6C6C6",
                    }}
                  />
                  <Typography sx={{ color: imgBoxColor }}>
                    {t("inventory.uploadImage")}
                  </Typography>
                </div>
              )}
            </Box> */}

            {/* Name Fields */}
            <Box sx={{ display: "flex", gap: 2 }}>
              <StyledTextField
                label={t("loginPage.firstname")}
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                error={errors.firstName}
                inputBoxColor={inputBoxColor}
                required
              />

              <StyledTextField
                label={t("loginPage.lastname")}
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                error={errors.lastName}
                inputBoxColor={inputBoxColor}
                required
              />
            </Box>

            {/* Name and email */}
            <Box sx={{ display: "flex", gap: 2 }}>
              <StyledTextField
                label={t("loginPage.username")}
                name="login"
                value={formData.login}
                onChange={handleChange}
                error={errors.login}
                inputBoxColor={inputBoxColor}
                required
                disabled={!!selectedUser}
              />

              <StyledTextField
                label={t("loginPage.email")}
                name="email"
                value={formData.email}
                onChange={handleChange}
                error={errors.email}
                inputBoxColor={inputBoxColor}
                required
              />
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                flex: 1,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  borderRadius: "6px",
                  overflow: "hidden",
                  backgroundColor: inputBoxColor,
                  height: "50px",
                }}
              >
                <Autocomplete
                  options={countryCodes}
                  getOptionLabel={(option) =>
                    `${option.name} (${option.dial_code})`
                  }
                  value={countryCode}
                  onChange={(e, newValue) => {
                    if (newValue) setCountryCode(newValue);
                  }}
                  isOptionEqualToValue={(option, value) =>
                    option.dial_code === value.dial_code
                  }
                  sx={{
                    minWidth: 185,
                    "& .MuiInputBase-root": {
                      height: "48px",
                      borderRight: `1px solid ${borderColor2}`,
                      borderRadius: 0,
                      backgroundColor: "transparent",
                      px: 2,
                    },
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="standard"
                      InputProps={{
                        ...params.InputProps,
                        disableUnderline: true,
                        style: {
                          color: errors.mobile ? "red" : "inherit",
                        },
                      }}
                    />
                  )}
                />
                <InputBase
                  placeholder={`${t("membershipCards.mobileNumber")} *`}
                  value={mobileNumber}
                  onChange={(e) => {
                    const digitsOnly = e.target.value.replace(/\D/g, "");
                    setMobileNumber(digitsOnly);
                    if (errors.mobile) {
                      setErrors((prev) => ({ ...prev, mobile: "" }));
                      setGeneralError("");
                    }
                  }}
                  inputProps={{
                    style: {
                      color: errors.mobile ? "red" : "inherit",
                    },
                  }}
                  sx={{ px: 2, flex: 1, height: "100%" }}
                />
              </Box>
            </Box>

            <StyledTextField
              label={t("users.address")}
              name="address"
              value={formData.address}
              onChange={handleChange}
              error={errors.address}
              inputBoxColor={inputBoxColor}
              required
            />
            {/* Role */}
            <Box sx={{ display: "flex", gap: 2 }}>
              <StyledTextField
                label={t("users.role")}
                name="authorities"
                value={formData.authorities?.[0] || ""}
                onChange={handleChange}
                error={errors.authorities}
                inputBoxColor={inputBoxColor}
                required
              >
                <MenuItem value="ROLE_ADMIN">{t("users.Admin")}</MenuItem>
                <MenuItem value="ROLE_EMPLOYEE">{t("users.Employee")}</MenuItem>
              </StyledTextField>
            </Box>

            {generalError && (
              <Typography
                color="error"
                variant="body2"
                sx={{ textAlign: "center", marginTop: 1 }}
              >
                {generalError}
              </Typography>
            )}
            {/* Action Buttons */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
              <button
                type="button"
                style={{ color: btnColor, borderColor: btnColor }}
                className="cancel-btn"
                onClick={handleClose}
              >
                {t("membershipCards.cancel")}
              </button>
              <button
                type="button"
                style={{ background: btnColor }}
                className="save-btn"
                onClick={handleSave}
              >
                {t("membershipCards.save")}
              </button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Use Notification Component */}
      <SuccessNotification
        open={successNotification}
        message={t(
          selectedUser
            ? "membershipCards.successMessage"
            : "users.userSuccessMessage",
        )}
        severity="success"
        onClose={() => setSuccessNotification(false)}
      />
    </>
  );
}

export default UsersModal;
